import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Users, CalendarDays, ArrowUpDown, Gamepad2, User, Bot, Handshake, Globe } from 'lucide-react';
import Layout from '../components/Layout';
import { readGameStats } from '../gameStats';

const numberFormat = new Intl.NumberFormat('en-US');

function formatDay(timestamp) {
  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function TrendChart({ data }) {
  const max = Math.max(...data.map((d) => d.pageviews), 1);

  return (
    <div className="flex items-end gap-[3px] h-40" role="img" aria-label="Daily page views over the selected period">
      {data.map((day) => (
        <div key={day.timestamp} className="group relative flex-1 flex flex-col justify-end h-full min-w-[4px]">
          <div
            className="w-full rounded-t bg-blue-600/70 dark:bg-blue-400/70 group-hover:bg-blue-600 dark:group-hover:bg-blue-400 transition-colors"
            style={{ height: `${(day.pageviews / max) * 100}%`, minHeight: day.pageviews > 0 ? '2px' : 0 }}
          />
          <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white dark:bg-gray-100 dark:text-gray-900 z-10">
            {formatDay(day.timestamp)}: {numberFormat.format(day.pageviews)} views
          </div>
        </div>
      ))}
    </div>
  );
}

const StatCard = ({ icon, label, value }) => (
  <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-6">
    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
      {icon}
      {label}
    </div>
    <div className="text-3xl font-semibold text-black dark:text-gray-200 tabular-nums">
      {value}
    </div>
  </div>
);

const PATH_LABELS = {
  '/': 'Home',
  '/about': 'About',
  '/blog': 'Blog',
  '/stats': 'Stats',
};

const PAGE_SORTS = {
  views: (a, b) => b.pageviews - a.pageviews,
  visitors: (a, b) => b.visitors - a.visitors,
  path: (a, b) => a.requestPath.localeCompare(b.requestPath),
};

function pathLabel(requestPath) {
  return PATH_LABELS[requestPath] || requestPath;
}

const Stats = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageSort, setPageSort] = useState('views');
  const [gameStats] = useState(readGameStats);
  const [allTime, setAllTime] = useState(null);

  useEffect(() => {
    fetch('/api/count')
      .then((response) => (response.ok ? response.json() : null))
      .then(setAllTime)
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(`/api/stats?days=${days}`)
      .then(async (response) => {
        const body = await response.json();
        if (!response.ok) throw new Error(body.error || `Request failed (${response.status})`);
        return body;
      })
      .then((body) => {
        if (!cancelled) setData(body);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [days]);

  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-4 md:px-8 xl:px-24 2xl:px-32">
        <div className="mb-12">
          <h1 className="text-4xl tracking-tight text-black dark:text-gray-200 sm:text-5xl mb-4">
            Stats
          </h1>
          <p className="text-base text-gray-600 dark:text-gray-400">
            Page views and visitors, powered by Vercel Web Analytics.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 p-4 mb-8 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {loading && !data ? (
          <div className="text-gray-600 dark:text-gray-400 text-base py-12 text-center">
            Loading…
          </div>
        ) : data && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
              <StatCard icon={<Eye className="h-4 w-4" />} label="Total page views" value={numberFormat.format(data.totals?.pageviews ?? 0)} />
              <StatCard icon={<Users className="h-4 w-4" />} label="Total visitors" value={numberFormat.format(data.totals?.visitors ?? 0)} />
            </div>

            <section className="mb-10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="flex items-center gap-2 text-xl font-bold uppercase tracking-widest text-black dark:text-gray-200">
                  <CalendarDays className="h-5 w-5" />
                  Daily page views
                </h2>
                <div className="flex gap-1 rounded-lg border border-gray-200 dark:border-gray-800 p-1">
                  {[7, 30].map((option) => (
                    <button
                      key={option}
                      onClick={() => setDays(option)}
                      className={`rounded px-3 py-1 text-sm transition-colors ${
                        days === option
                          ? 'bg-blue-600 text-white dark:bg-blue-500'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                      }`}
                    >
                      {option}d
                    </button>
                  ))}
                </div>
              </div>
              {data.daily.length > 0 ? (
                <TrendChart data={data.daily} />
              ) : (
                <p className="text-gray-600 dark:text-gray-500 text-base">
                  No traffic recorded in this period yet.
                </p>
              )}
            </section>

            <section className="mb-12">
              <h2 className="text-xl font-bold uppercase tracking-widest text-black dark:text-gray-200 mb-4">
                Pages ({days} days)
              </h2>
              {data.topPages.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-800 text-left text-gray-600 dark:text-gray-400">
                      {[['path', 'Page'], ['views', 'Views'], ['visitors', 'Visitors']].map(([key, label]) => (
                        <th
                          key={key}
                          className={`py-2 font-medium ${key !== 'path' ? 'text-right' : ''}`}
                        >
                          <button
                            onClick={() => setPageSort(key)}
                            className={`hover:text-gray-900 dark:hover:text-gray-200 transition-colors ${
                              pageSort === key ? 'text-blue-600 dark:text-blue-400' : ''
                            } ${key !== 'path' ? 'w-full flex justify-end items-center gap-1' : 'flex items-center gap-1'}`}
                          >
                            {label}
                            <ArrowUpDown className={`h-3 w-3 ${pageSort === key ? 'opacity-100' : 'opacity-0'}`} />
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...data.topPages].sort(PAGE_SORTS[pageSort]).map((page) => {
                      const label = pathLabel(page.requestPath);
                      const to = page.requestPath.split('?')[0].replace(/\/$/, '') || '/';
                      return (
                        <tr key={page.requestPath} className="border-b border-gray-100 dark:border-gray-800/50">
                          <td className="py-2">
                            <Link to={to} className="group">
                              <span className="text-gray-800 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                {label}
                              </span>
                              {label !== page.requestPath && (
                                <span className="ml-2 font-mono text-xs text-gray-500 dark:text-gray-500">
                                  {page.requestPath}
                                </span>
                              )}
                            </Link>
                          </td>
                          <td className="py-2 text-right tabular-nums text-gray-800 dark:text-gray-300">
                            {numberFormat.format(page.pageviews)}
                          </td>
                          <td className="py-2 text-right tabular-nums text-gray-800 dark:text-gray-300">
                            {numberFormat.format(page.visitors)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <p className="text-gray-600 dark:text-gray-500 text-base">
                  No page data recorded in this period yet.
                </p>
              )}
            </section>
          </>
        )}

        {((data?.lifetimePages?.length ?? 0) > 0 || (allTime?.games.played ?? 0) > 0) && (
          <section className="mb-12">
            <h2 className="flex items-center gap-2 text-xl font-bold uppercase tracking-widest text-black dark:text-gray-200 mb-1">
              <Globe className="h-5 w-5" />
              All-time
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Across all visitors. Page views are Vercel Analytics lifetime counts; games are counted since 21 Sep 2026.
            </p>
            {(allTime?.games.played ?? 0) > 0 && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard icon={<Gamepad2 className="h-4 w-4" />} label="4-In-A-Row played" value={numberFormat.format(allTime.games.played)} />
                <StatCard icon={<User className="h-4 w-4" />} label="Human wins" value={numberFormat.format(allTime.games.human)} />
                <StatCard icon={<Bot className="h-4 w-4" />} label="AI wins" value={numberFormat.format(allTime.games.ai)} />
                <StatCard icon={<Handshake className="h-4 w-4" />} label="Draws" value={numberFormat.format(allTime.games.draws)} />
              </div>
            )}
            {(data?.lifetimePages?.length ?? 0) > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-800 text-left text-gray-600 dark:text-gray-400">
                    <th className="py-2 font-medium">Page</th>
                    <th className="py-2 font-medium text-right">Total views</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lifetimePages.map((page) => (
                    <tr key={page.requestPath} className="border-b border-gray-100 dark:border-gray-800/50">
                      <td className="py-2">
                        <span className="text-gray-800 dark:text-gray-300">{pathLabel(page.requestPath)}</span>
                        {pathLabel(page.requestPath) !== page.requestPath && (
                          <span className="ml-2 font-mono text-xs text-gray-500">{page.requestPath}</span>
                        )}
                      </td>
                      <td className="py-2 text-right tabular-nums text-gray-800 dark:text-gray-300">
                        {numberFormat.format(page.pageviews)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        )}

        {gameStats.played > 0 && (
          <section className="mb-12">
            <h2 className="flex items-center gap-2 text-xl font-bold uppercase tracking-widest text-black dark:text-gray-200 mb-1">
              <Gamepad2 className="h-5 w-5" />
              4-In-A-Row
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Games played in this browser.
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={<Gamepad2 className="h-4 w-4" />} label="Games played" value={numberFormat.format(gameStats.played)} />
              <StatCard icon={<User className="h-4 w-4" />} label="Your wins" value={numberFormat.format(gameStats.human)} />
              <StatCard icon={<Bot className="h-4 w-4" />} label="AI wins" value={numberFormat.format(gameStats.ai)} />
              <StatCard icon={<Handshake className="h-4 w-4" />} label="Draws" value={numberFormat.format(gameStats.draws)} />
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
};

export default Stats;
